import { DateOnly } from '@if/core/date'
import { ECreditApplicationStatus, EFinancingProgram, ELoanPurpose, EPaymentPlan, EProvince } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  ActionButton,
  BodyText,
  ControlledCheckbox,
  FormContainer,
  type FormSchema,
  MultiTranslatedError,
  useCan,
  useFormContainerContext,
  useFormDialog,
} from '@if/mui'
import { Stack } from '@mui/material'
import { access } from '@src/access'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { validateCreditApplicationCanBeSubmittedForReview } from '@src/data/credit-app-validations'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { FinancingProgramConfigs } from '@src/types/Constants'
import {
  CreditApplication,
  DraftCreditApplicationDto,
  buildDraftCreditApplicationSchema,
  buildFullCreditApplicationSchema,
} from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ApplicationFormBody from './ApplicationFormBody'
import GetCreditContentConsentText from './creditApplicationForm-selectors'

interface Props {
  /** `create` for a new application; `edit` when reopening an existing one (dirty-gates the submit). */
  mode: 'create' | 'edit'
  merchant: Merchant | null
  creditApplication: DraftCreditApplicationDto
  onSave: (data: Partial<CreditApplication>) => Promise<void>
  financingProgramId: EFinancingProgram
  lockApplicantIncomeAndExpenses?: boolean
  lockCoApplicantIncomeAndExpenses?: boolean
  isPrequalComplete: boolean
  isApplicantSinNeeded: boolean | null
  isCoapplicantSinNeeded: boolean | null
}

const enum DialogComponentRender {
  prequalify,
  qualify,
  submit,
}

// The loaded application carries `birthDate` as a wire string; convert a non-empty
// value to `DateOnly` for `ControlledDateOnlyPicker`, normalizing empty/absent to null.
const toDateOnly = (value: string | Date | null | undefined): DateOnly | null => (value ? new DateOnly(value) : null)

const ApplicationForm = ({
  mode,
  merchant,
  creditApplication,
  onSave,
  financingProgramId,
  lockApplicantIncomeAndExpenses,
  lockCoApplicantIncomeAndExpenses,
  isPrequalComplete,
  isApplicantSinNeeded,
  isCoapplicantSinNeeded,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const programConfig = programConfigByFinancingProgramId(financingProgramId)
  // `?addCoApp=true` deep-links into adding a co-applicant (only where the program supports one).
  const addCoApp = Boolean(searchParams.get('addCoApp')) && programConfig.supportsCoApplicant

  // Loan min/max feed the yup `$minLoanValue`/`$maxLoanValue` context (Personal only fetches a merchant config).
  const shouldFetchMerchantConfig = financingProgramId === EFinancingProgram.Personal && !!merchant?.id
  const [merchantFinancingConfig] = useMerchantFinancingConfig(
    shouldFetchMerchantConfig ? merchant?.id : null,
    financingProgramId,
  )
  const isMedicardServiceCategory =
    merchant?.serviceCategory === ELoanPurpose.Medical ||
    merchant?.serviceCategory === ELoanPurpose.Dentistry ||
    merchant?.serviceCategory === ELoanPurpose.Aesthetics
  const defaultConfig = FinancingProgramConfigs[financingProgramId]
  const categoryBasedMax =
    financingProgramId === EFinancingProgram.Personal && isMedicardServiceCategory
      ? defaultConfig.maxLoanAmountMedicard
      : defaultConfig.maxLoanAmount
  const maxLoanAmount = Math.min(categoryBasedMax, merchantFinancingConfig?.maximumLoanAmount ?? categoryBasedMax)
  const minLoanAmount = merchantFinancingConfig?.minimumLoanAmount ?? defaultConfig.minLoanAmount
  const useExactMonthsUnderOneYear = useCan(access.exactMonthsUnderOneYear)

  const isQualify =
    (!programConfig.useSoftHit && programConfig.useHardHit) ||
    programConfig.useAlwaysSubmit ||
    creditApplication.consentHardHit
  const isDraft = creditApplication.status === ECreditApplicationStatus.Draft

  const [hasPrequalifyBeenPressed, setHasPrequalifyBeenPressed] = useState(false)
  const [coappChecked, setCoappChecked] = useState<boolean>(!!creditApplication.coApplicant || addCoApp)

  // The banner-selected merchant lives in the body (it depends on a watched field); it feeds business validation.
  const selectedMerchantRef = useRef<Merchant | null>(merchant)

  // The consent step is a small "must acknowledge" form dialog (an `agreed` checkbox that gates confirm),
  // opened after validation passes. Its content varies by the render mode + whether consent is even needed.
  const consentSchema = useMemo(
    () => yup.object({ agreed: yup.boolean().oneOf([true], t('submission.mustAgreeToTerms')) }),
    [t],
  )
  const consentDialog = useFormDialog<{ agreed: boolean }, { render: DialogComponentRender; noConsentNeeded: boolean }>(
    {
      title: t('submission.submission'),
      confirmLabel: isQualify ? t('submission.submit') : t('submission.submitPrequal'),
      schema: consentSchema,
      defaultValues: {},
      maxWidth: 'sm',
      content: ({ form, props }) => (
        <Stack spacing={2}>
          {props.noConsentNeeded ? (
            <BodyText>{t('common.submitConfirmation')}</BodyText>
          ) : props.render === DialogComponentRender.prequalify ? (
            <BodyText>{t('submission.creditAgreementMessage')}</BodyText>
          ) : props.render === DialogComponentRender.qualify ? (
            <GetCreditContentConsentText financingProgram={financingProgramId} />
          ) : (
            <BodyText>{t('common.submitConfirmation')}</BodyText>
          )}
          <ControlledCheckbox
            name="agreed"
            control={form.control}
            label={props.render === DialogComponentRender.submit ? t('common.yes') : t('submission.agreeToTerms')}
          />
        </Stack>
      ),
    },
  )

  const formSetup = useMemo(
    () => ({
      schema: buildFullCreditApplicationSchema(),
      defaultValues: {
        ...creditApplication,
        // `birthDate` is a `yup.dateOnly()` field, but the loaded application carries
        // it as a wire string — normalize to `DateOnly` so the picker accepts it.
        applicant: {
          ...creditApplication.applicant,
          birthDate: toDateOnly(creditApplication.applicant?.birthDate),
        },
        coApplicant: creditApplication.coApplicant
          ? { ...creditApplication.coApplicant, birthDate: toDateOnly(creditApplication.coApplicant.birthDate) }
          : creditApplication.coApplicant,
        financingProgramId,
        merchantPaymentPlanId: creditApplication.merchantPaymentPlanId ?? EPaymentPlan.RegularDailyInterests,
      },
      context: { maxLoanValue: maxLoanAmount, minLoanValue: minLoanAmount, useExactMonthsUnderOneYear },
    }),
    [creditApplication, financingProgramId, maxLoanAmount, minLoanAmount, useExactMonthsUnderOneYear],
  )
  const useFormSetup = () => formSetup

  const draftSaveSchema = useMemo<FormSchema>(
    () =>
      buildDraftCreditApplicationSchema().shape({
        requestedLoanAmount: yup.double().when('financingProgramId', (financingProgramId, schema) => {
          const config = programConfigByFinancingProgramId(financingProgramId[0] as EFinancingProgram)
          return (config?.requestedLoanAmountIsRequired ?? true) ? schema.required() : yup.double()
        }),
      }),
    [],
  )

  const handlePrequalify = async (values: DraftCreditApplicationDto) => {
    setHasPrequalifyBeenPressed(true)

    const cErrors = validateCreditApplicationCanBeSubmittedForReview(
      values as CreditApplication,
      selectedMerchantRef.current,
    )
    if (cErrors.length > 0) {
      throw new MultiTranslatedError(cErrors.map((e) => ({ key: e.messageKey, options: e.params })))
    }

    let render = DialogComponentRender.prequalify
    const stateIso = values.applicant?.currentAddress?.stateIso as EProvince | undefined
    if (isQualify) {
      const hardHitForProvince = programConfig.useHardHitForSpecificProvince?.includes(stateIso!)
      render =
        creditApplication.consentHardHit || (!programConfig.useHardHit && !hardHitForProvince)
          ? DialogComponentRender.submit
          : DialogComponentRender.qualify
    }
    const noConsentNeeded = financingProgramId === EFinancingProgram.Products && stateIso !== EProvince.Quebec

    // Await the consent dialog + save so the submit's loading and any save error flow through the FormContainer.
    const consent = await consentDialog.create({ render, noConsentNeeded })
    if (!consent) return false
    await onSave({
      ...(values as CreditApplication),
      status: ECreditApplicationStatus.Active,
      consentSoftHit: true,
      consentHardHit: render === DialogComponentRender.qualify || render === DialogComponentRender.submit,
    })
  }

  const handleCancel = () => {
    if (!creditApplication.id) navigate('/credits')
    else navigate(`/credits/${financingProgramId}/${creditApplication.id}`)
  }

  return (
    <FormContainer
      mode={mode}
      useFormSetup={useFormSetup}
      onSubmit={handlePrequalify}
      pageActions
      secondaryTone="destructive"
      submitLabel={isQualify ? t('common.submit') : t('common.prequalify')}
      secondaryLabel={t('common.cancel')}
      onSecondary={handleCancel}
      actions={isDraft ? <SaveDraftAction onSave={onSave} schema={draftSaveSchema} /> : undefined}
    >
      <ApplicationFormBody
        mode={mode}
        financingProgramId={financingProgramId}
        editDisabled={!!creditApplication.editLocked}
        isPrequalComplete={isPrequalComplete}
        isApplicantSinNeeded={isApplicantSinNeeded}
        isCoapplicantSinNeeded={isCoapplicantSinNeeded}
        lockApplicantIncomeAndExpenses={lockApplicantIncomeAndExpenses}
        lockCoApplicantIncomeAndExpenses={lockCoApplicantIncomeAndExpenses}
        hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
        coappChecked={coappChecked}
        setCoappChecked={setCoappChecked}
        addCoApp={addCoApp}
        hasExistingCoApplicant={creditApplication.coApplicant != null}
        onSelectedMerchantChange={(m) => {
          selectedMerchantRef.current = m
        }}
      />
    </FormContainer>
  )
}

// Save-draft persists the current values without the full prequalify validation — but a draft still has a minimum
// (the draft schema's required fields). `runActionWithSchema` validates the current values against that lighter
// schema, surfaces any failure as an INLINE field error automatically, and only runs the save when it passes —
// all through the FormContainer's own loading + error machinery.
const SaveDraftAction = ({
  onSave,
  schema,
}: {
  onSave: (data: Partial<CreditApplication>) => Promise<void>
  schema: FormSchema
}) => {
  const { t } = useTranslation()
  const { runActionWithSchema, loading } = useFormContainerContext<DraftCreditApplicationDto>()
  const saveDraft = () =>
    runActionWithSchema(schema, (values) =>
      onSave({ ...(values as CreditApplication), status: ECreditApplicationStatus.Draft }),
    )
  return (
    <ActionButton id="saveDraft" type="button" emphasis="normal" onClick={() => void saveDraft()} loading={loading}>
      {t('common.saveDraft')}
    </ActionButton>
  )
}

export default ApplicationForm
